"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useStartOrderConfirmedQueryData } from "./useStartOrderConfirmedQueryData";
import { pushPurchaseEvent } from "@/utils/gtm";
import { calculateOrderTotal } from "@/components/screen/start-order/components/Checkout/utils";
import {
  CERTIFIED_ORDER_DELIVERY_OPTIONS,
  STANDARD_ORDER_DELIVERY_OPTIONS,
  TRANSLATION_RATES,
} from "@/components/screen/start-order/components/Checkout/constants";

/**
 * Hook that automatically pushes GTM purchase event when order confirmation page loads
 * Extracts all necessary data from URL query parameters and creates proper item structure
 */
export const useGTMPurchaseEvent = () => {
  const searchParams = useSearchParams();
  const orderId = searchParams.get("orderId") ?? undefined;
  const orderData = useStartOrderConfirmedQueryData();

  useEffect(() => {
    if (!orderId) {
      return;
    }

    const orderTotal = calculateOrderTotal({
      translationType: orderData.translationType as any,
      pageCount: orderData.pageCount,
      wordCount: orderData.wordCount,
      delivery: orderData.delivery as any,
    });

    // Create items array for GTM
    const items = createGTMItems(orderData);

    pushPurchaseEvent({
      transaction_id: orderId,
      value: orderTotal,
      currency: "USD",
      email: orderData.email,
      first_name: orderData.firstName,
      last_name: orderData.lastName,
      items: items
    });
  }, [orderId, orderData]);
};

/**
 * Creates GTM items array from order data
 * Handles both main translation service and delivery add-ons
 */
const createGTMItems = (orderData: ReturnType<typeof useStartOrderConfirmedQueryData>) => {
  const items = [];

  // Main translation service item
  const translationCost = orderData.isCertified 
    ? orderData.pageCount * TRANSLATION_RATES.certified
    : orderData.wordCount * TRANSLATION_RATES.standard;

  items.push({
    item_category: `${orderData.fromLanguage} to ${orderData.toLanguage} Translation`,
    item_name: orderData.isCertified ? "Certified Translation" : "Standard Translation",
    price: Number(translationCost.toFixed(2)),
    quantity: orderData.isCertified ? orderData.pageCount : orderData.wordCount
  });

  // Add delivery options as separate items
  const deliveryOptions = orderData.isCertified 
    ? CERTIFIED_ORDER_DELIVERY_OPTIONS
    : STANDARD_ORDER_DELIVERY_OPTIONS;

  orderData.delivery.forEach((deliveryOption) => {
    const option = deliveryOptions.find(opt => opt.value === deliveryOption);
    if (option && option.rate) {
      let itemPrice = option.rate;
      let itemQuantity = 1;

      // Special handling for expedited turnaround in certified translations
      if (orderData.isCertified && deliveryOption === 'expeditedTurnaround') {
        itemPrice = option.rate * orderData.pageCount;
        itemQuantity = orderData.pageCount;
      }
      // Special handling for expedited delivery in standard translations
      else if (!orderData.isCertified && deliveryOption === 'expeditedDelivery') {
        const standardPages = Math.ceil(orderData.wordCount / 250);
        itemPrice = option.rate * standardPages;
        itemQuantity = standardPages;
      }

      items.push({
        item_name: option.label,
        item_category: "Service Add-on",
        price: Number(itemPrice.toFixed(2)),
        quantity: itemQuantity
      });
    }
  });

  return items;
};
